import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Users,
  Clock,
  Target,
  Activity,
  BarChart3,
  Eye,
  Star,
  StarOff,
  Calendar,
  Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CandlestickChart from '../components/CandlestickChart';

export default function CoinDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  
  // 模拟币种数据
  const [coinData, setCoinData] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [bettingHistory, setBettingHistory] = useState([]);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [timeframe, setTimeframe] = useState('1h');

  useEffect(() => {
    // 模拟获取币种详细数据
    const mockCoinData = {
      'BTC': {
        name: 'Bitcoin',
        symbol: 'BTC',
        currentPrice: 67234,
        priceChange: '+2.34%',
        priceChangeValue: 1534,
        marketCap: '$1.32T',
        volume24h: '$28.5B',
        high24h: 68500,
        low24h: 65800,
        description: 'Bitcoin is the world\'s first cryptocurrency and remains the most valuable and widely recognized digital asset.',
        tags: ['Major', 'Store of Value'],
        totalBets: 1247,
        activeBets: 89,
        watchlistCount: 1200,
        // 新增的基本信息
        launchDate: '2009-01-03',
        circulatingSupply: '19.7M BTC',
        maxSupply: '21M BTC',
        allTimeHigh: '$69,045',
        allTimeLow: '$0.0009',
        athDate: '2021-11-10',
        atlDate: '2010-07-17'
      },
      'ETH': {
        name: 'Ethereum',
        symbol: 'ETH',
        currentPrice: 3456,
        priceChange: '-1.23%',
        priceChangeValue: -43,
        marketCap: '$415B',
        volume24h: '$15.2B',
        high24h: 3520,
        low24h: 3380,
        description: 'Ethereum is a decentralized platform that runs smart contracts and serves as the foundation for thousands of decentralized applications.',
        tags: ['Major', 'DeFi', 'Smart Contracts'],
        totalBets: 856,
        activeBets: 67,
        watchlistCount: 890,
        // 新增的基本信息
        launchDate: '2015-07-30',
        circulatingSupply: '120.4M ETH',
        maxSupply: 'No Limit',
        allTimeHigh: '$4,878',
        allTimeLow: '$0.42',
        athDate: '2021-11-10',
        atlDate: '2015-10-20'
      }
    };

    const coin = mockCoinData[symbol] || mockCoinData['BTC'];
    setCoinData(coin);
    setIsWatchlisted(Math.random() > 0.5);

    // 生成模拟K线数据
    const generatePriceHistory = () => {
      const data = [];
      const basePrice = coin.currentPrice;
      const now = new Date();
      let currentPrice = basePrice;
      
      for (let i = 23; i >= 0; i--) {
        const time = new Date(now.getTime() - i * 60 * 60 * 1000);
        
        // 生成K线数据：开盘价、最高价、最低价、收盘价
        const open = currentPrice;
        const priceChange = (Math.random() - 0.5) * 0.04; // 4%的最大变化
        const close = open * (1 + priceChange);
        
        // 生成最高价和最低价
        const volatility = Math.abs(priceChange) + Math.random() * 0.02;
        const high = Math.max(open, close) * (1 + volatility);
        const low = Math.min(open, close) * (1 - volatility);
        
        // 确保价格不会偏离基准价格太远
        const maxDeviation = basePrice * 0.12;
        if (Math.abs(close - basePrice) > maxDeviation) {
          currentPrice = basePrice + (Math.random() - 0.5) * maxDeviation;
        } else {
          currentPrice = close;
        }
        
        // 生成交易量（与价格波动相关）
        const priceVolatility = Math.abs(priceChange);
        const baseVolume = 150000;
        const volumeMultiplier = 1 + priceVolatility * 8;
        const volume = baseVolume * volumeMultiplier * (0.6 + Math.random() * 0.8);
        
        data.push({
          time: time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          open: Math.round(open * 100) / 100,
          high: Math.round(high * 100) / 100,
          low: Math.round(low * 100) / 100,
          close: Math.round(close * 100) / 100,
          volume: Math.round(volume),
          // 用于简单显示的价格（收盘价）
          price: Math.round(close * 100) / 100
        });
      }
      
      // 确保最后一个收盘价接近当前价格
      if (data.length > 0) {
        data[data.length - 1].close = basePrice;
        data[data.length - 1].price = basePrice;
      }
      
      return data;
    };

    setPriceHistory(generatePriceHistory());

    // 生成模拟下注历史
    const generateBettingHistory = () => {
      const bets = [];
      const now = new Date();
      
      for (let i = 0; i < 20; i++) {
        const time = new Date(now.getTime() - i * 15 * 60 * 1000);
        bets.push({
          id: i + 1,
          user: `User${Math.floor(Math.random() * 1000)}`,
          direction: Math.random() > 0.5 ? 'up' : 'down',
          amount: Math.floor(Math.random() * 500) + 50,
          entryPrice: coin.currentPrice + (Math.random() - 0.5) * 100,
          timestamp: time,
          duration: ['5m', '15m', '30m', '1h'][Math.floor(Math.random() * 4)],
          status: ['active', 'won', 'lost'][Math.floor(Math.random() * 3)]
        });
      }
      return bets;
    };

    setBettingHistory(generateBettingHistory());
  }, [symbol]);

  const handleWatchlistToggle = () => {
    setIsWatchlisted(!isWatchlisted);
  };

  const formatPrice = (price) => {
    if (price >= 1000) {
      return `$${price.toLocaleString()}`;
    }
    return `$${price.toFixed(4)}`;
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!coinData) {
    return (
      <div className="min-h-screen bg-plypto-bg flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">{coinData.symbol[0]}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{coinData.name}</h1>
                <p className="text-gray-400">{coinData.symbol}/USD</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleWatchlistToggle}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              isWatchlisted 
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
                : 'bg-white/10 text-gray-400 border border-white/20 hover:bg-white/20'
            }`}
          >
            {isWatchlisted ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
            <span>{isWatchlisted ? 'Watchlisted' : 'Add to Watchlist'}</span>
          </button>
        </div>

        {/* Price Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Current Price</span>
              <DollarSign className="w-4 h-4 text-green-400" />
            </div>
            <div className="text-2xl font-bold text-white">{formatPrice(coinData.currentPrice)}</div>
            <div className={`text-sm ${coinData.priceChange.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
              {coinData.priceChange} ({coinData.priceChangeValue > 0 ? '+' : ''}${coinData.priceChangeValue})
            </div>
          </div>

          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">24h High/Low</span>
              <Activity className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-lg font-bold text-white">{formatPrice(coinData.high24h)}</div>
            <div className="text-sm text-gray-400">{formatPrice(coinData.low24h)}</div>
          </div>

          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Market Cap</span>
              <BarChart3 className="w-4 h-4 text-purple-400" />
            </div>
            <div className="text-lg font-bold text-white">{coinData.marketCap}</div>
            <div className="text-sm text-gray-400">24h Vol: {coinData.volume24h}</div>
          </div>

          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Active Bets</span>
              <Users className="w-4 h-4 text-orange-400" />
            </div>
            <div className="text-lg font-bold text-white">{coinData.activeBets}</div>
            <div className="text-sm text-gray-400">Total: {coinData.totalBets}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Left Column - Chart (占3列) */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Combined Price & Volume Chart */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Price & Volume Chart</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-0.5 bg-blue-400"></div>
                      <span className="text-xs text-gray-400">Price</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-2 bg-purple-500"></div>
                      <span className="text-xs text-gray-400">Volume</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {['1h', '4h', '1d', '1w'].map(tf => (
                      <button
                        key={tf}
                        onClick={() => setTimeframe(tf)}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          timeframe === tf
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {tf}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* 蜡烛图 */}
              <div className="h-80 mb-4">
                <div className="text-sm text-gray-400 mb-2">Price (USD)</div>
                <div className="w-full h-full">
                  <CandlestickChart 
                    data={priceHistory} 
                    width={800} 
                    height={300}
                  />
                </div>
              </div>
              
              {/* 交易量柱状图 - 暂时隐藏 */}
              {/* <div className="h-48">
                <div className="text-sm text-gray-400 mb-2">Volume</div>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={priceHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="time" 
                      stroke="#9CA3AF"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#9CA3AF"
                      fontSize={12}
                      tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                      formatter={(value) => [`${(value / 1000000).toFixed(2)}M`, 'Volume']}
                    />
                    <Bar 
                      dataKey="volume" 
                      fill="#8B5CF6"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div> */}
            </div>
            
            {/* Recent Bets - 列表形式 */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Recent Betting Activity</h3>
                <Eye className="w-5 h-5 text-gray-400" />
              </div>
              
              {/* Table Header */}
              <div className="grid grid-cols-6 gap-4 pb-3 mb-4 border-b border-white/10 text-sm text-gray-400">
                <div>User</div>
                <div>Direction</div>
                <div>Amount</div>
                <div>Entry Price</div>
                <div>Duration</div>
                <div>Status</div>
              </div>
              
              {/* Table Body */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {bettingHistory.slice(0, 15).map(bet => (
                  <div key={bet.id} className="grid grid-cols-6 gap-4 py-3 px-2 rounded-lg hover:bg-white/5 transition-colors">
                    {/* User */}
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        bet.direction === 'up' ? 'bg-green-400' : 'bg-red-400'
                      }`}></div>
                      <span className="text-white text-sm font-medium">{bet.user}</span>
                    </div>
                    
                    {/* Direction */}
                    <div className={`text-sm font-medium ${
                      bet.direction === 'up' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {bet.direction === 'up' ? '↗ LONG' : '↘ SHORT'}
                    </div>
                    
                    {/* Amount */}
                    <div className="text-white text-sm font-bold">
                      ${bet.amount}
                    </div>
                    
                    {/* Entry Price */}
                    <div className="text-gray-300 text-sm">
                      ${bet.entryPrice.toFixed(2)}
                    </div>
                    
                    {/* Duration */}
                    <div className="text-gray-400 text-sm">
                      {bet.duration}
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        bet.status === 'active' ? 'bg-blue-500/20 text-blue-400' :
                        bet.status === 'won' ? 'bg-green-500/20 text-green-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {bet.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(bet.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Info (占1列) */}
          <div className="space-y-6">
            
            {/* Coin Info */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-4">About {coinData.name}</h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                {coinData.description}
              </p>
              
              {/* 基本信息 */}
              <div className="space-y-3 mb-5">
                <h4 className="text-lg font-medium text-white mb-2">Basic Information</h4>
                
                <div className="grid grid-cols-1 gap-1">
                  <div className="flex items-center justify-between py-1.5 border-b border-white/10">
                    <span className="text-gray-400 text-sm">Launch Date</span>
                    <span className="text-white text-sm font-medium">{coinData.launchDate}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-1.5 border-b border-white/10">
                    <span className="text-gray-400 text-sm">Circulating Supply</span>
                    <span className="text-white text-sm font-medium">{coinData.circulatingSupply}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-1.5 border-b border-white/10">
                    <span className="text-gray-400 text-sm">Max Supply</span>
                    <span className="text-white text-sm font-medium">{coinData.maxSupply}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-1.5 border-b border-white/10">
                    <span className="text-gray-400 text-sm">All-Time High</span>
                    <div className="text-right">
                      <div className="text-green-400 text-sm font-medium">{coinData.allTimeHigh}</div>
                      <div className="text-gray-500 text-xs">{coinData.athDate}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-gray-400 text-sm">All-Time Low</span>
                    <div className="text-right">
                      <div className="text-red-400 text-sm font-medium">{coinData.allTimeLow}</div>
                      <div className="text-gray-500 text-xs">{coinData.atlDate}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 标签 */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Categories</h4>
                <div className="flex flex-wrap gap-2">
                  {coinData.tags.map(tag => (
                    <span 
                      key={tag}
                      className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Watchlist Count</span>
                  <span className="text-white font-medium">{coinData.watchlistCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Total Predictions</span>
                  <span className="text-white font-medium">{coinData.totalBets}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Active Predictions</span>
                  <span className="text-white font-medium">{coinData.activeBets}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Success Rate</span>
                  <span className="text-green-400 font-medium">
                    {Math.floor(Math.random() * 30 + 50)}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Market Sentiment */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-4">Market Sentiment</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Bullish</span>
                    <span className="text-green-400 text-sm font-medium">68%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-green-400 h-2 rounded-full" style={{width: '68%'}}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Bearish</span>
                    <span className="text-red-400 text-sm font-medium">32%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-red-400 h-2 rounded-full" style={{width: '32%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
